"use client";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as Yup from "yup";
import { FormCheckbox, FormInput, HookFormSelect } from "../../../../form";
import { type AiFiltersFormType, type Translationtype } from "utils";
import { magicAIAspectRatioOptions } from "./magicAI.options";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef } from "react";

const aspectRatioNumberToString: Record<number, string> = {
  0: "16x9",
  1: "1x1",
  2: "9x16",
  3: "4x3",
  4: "3x4",
};

interface IMagicAiFilterProps {
  t: Translationtype;
  handleFieldChange: (value: AiFiltersFormType) => void;
  reservationDetails: any;
  showRandomFilter?: boolean;
}

export function MagicAIFilter({ t, handleFieldChange, reservationDetails, showRandomFilter = true }: IMagicAiFilterProps) {
  const lock = useRef<boolean>(false);
  const formSchema = Yup.object().shape({
    gender: Yup.string().nullable(),
    nameFilters: Yup.string().nullable(),
    aspectRatio: Yup.string().nullable(),
    randomFilter: Yup.boolean().nullable().default(false),
    videoOnly: Yup.boolean().nullable().default(false),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<AiFiltersFormType>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      gender: "woman",
      nameFilters: "",
      aspectRatio: "16x9",
      randomFilter: false,
      videoOnly: false,
    },
  });

  useEffect(() => {
    // Load initial values from reservationDetails
    if (reservationDetails && !lock.current) {
      if (reservationDetails.aspectRatio !== undefined && reservationDetails.aspectRatio !== null) {
        const aspectRatioString = aspectRatioNumberToString[reservationDetails.aspectRatio];
        if (aspectRatioString) {
          setValue("aspectRatio", aspectRatioString);
        }
      }
      if (reservationDetails.randomFilter !== undefined) {
        setValue("randomFilter", reservationDetails.randomFilter);
      }
      
      // Call handleFieldChange after values are loaded
      setTimeout(() => {
        handleFieldChange(watch());
      }, 0);
      
      lock.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationDetails]);

  const onSubmit: SubmitHandler<AiFiltersFormType> = (data) => {
    handleFieldChange(data);
  };

  return (
    <form onChange={handleSubmit(onSubmit)} className="w-fit">
      <div className="flex flex-col md:flex-row mb-3 md:mb-4 gap-4">
        <div className="flex flex-row gap-4">
          <FormInput
            t={t}
            register={register}
            errors={errors}
            name="nameFilters"
            title={t("Global.filterName")}
          />
        </div>
        <div className="flex flex-row gap-4">
          <HookFormSelect
            t={t}
            register={register}
            errors={errors}
            name="aspectRatio"
            options={magicAIAspectRatioOptions}
            title={t("Global.aspectRatio")}
          />
        </div>
        <div className="flex flex-row gap-3 items-center md:pt-[2rem]">
          <FormCheckbox
            t={t}
            register={register}
            errors={errors}
            name="videoOnly"
            title={t("MagicFilter.videoOnly")}
          />
        </div>
        {showRandomFilter && (
          <div className="flex flex-row gap-3 items-center md:pt-[2rem]">
            <FormCheckbox
              t={t}
              register={register}
              errors={errors}
              name="randomFilter"
              title={t("Reservation.randomFilter")}
            />
          </div>
        )}
      </div>
    </form>
  );
}
